/**
 * Status **component** sections for the Entity Details panel (ADR-024 §4 amended
 * 2026-07-24 and 2026-07-31; REQ-STATUS-027; Flow L §4).
 *
 * The relocated System Status component detail — moved out of the old in-panel
 * right rail (:file:`system-status/status-detail-pane.tsx`, removed) into the
 * shared Entity Details panel. Renders through the entity-details
 * :func:`SectionShell` (matching the Detection / Alert / query-record branches;
 * no section-visibility gear, mirroring query-record — decision 2026-07-24) while
 * reusing the System Status row primitives (mini-state / resource / metric /
 * sparkline) so the health, resource, domain, connections, and trend sections
 * read exactly as they did in the rail.
 *
 * Data comes from the **live** System Status feed the caller already resolved
 * (:func:`useResolvedSystemStatus` in the panel wrapper), not a per-id BFF route
 * (the Query flow depends on that route's absence — decision 4). The interface
 * management surface is hosted here (its own :class:`PeerManagementDialog`),
 * reached from the peer-manager node's own button or, pre-selected, from an interface
 * row.
 *
 * # What the 2026-07-31 hierarchy ruling changed here
 *
 * A component hosts N **interfaces**, and an interface hosts N **connections**
 * (feature-spec §2.2). So this pane:
 *
 *   - lists the node's configured **interfaces**, each row selecting a
 *     ``status-interface`` (REQ-STATUS-039) — which is also where the
 *     ``+K more`` overflow affordance lands the operator (REQ-STATUS-040), and
 *     where interfaces that compose no icon are still *represented*
 *     (REQ-STATUS-042);
 *   - keeps a **component-level connection roster** only on a node with no
 *     interface layer — today exactly ``Ingress``
 *     (:func:`hostsComponentLevelRoster`; REQ-STATUS-044). That roster is the
 *     one the deleted *SAPIENT Ingress Connections* satellite owned; re-homing
 *     rather than dropping it is what keeps REQ-STATUS-026's supersession
 *     lossless.
 *
 * Cross-references:
 *   - Flow L §4 (component detail) / §11 (suppressed flow visible); REQ-STATUS-027.
 *   - ADR-024 §4 (consolidated pane, no sub-tabs); REQ-UI-046 (status palette);
 *     REQ-UI-059 (keyboard/landmarks); ADR-036 §D-5/§D-7 (egress mgmt / standby).
 */
import { useState } from "react";
import {
  parseRollupState,
  resourcePercentBasis,
  type ComponentKind,
  type ResolvedComponent,
  type RollupState,
  type StatusConnection,
} from "@/system-status/status-model";
import {
  PROVIDER_KINDS,
  isCotsKind,
  isPeerNode,
  isProviderNode,
  type TopologyNode,
} from "@/system-status/status-topology";
import {
  MetricRow,
  MiniStateRow,
  ResourceRow,
  StatusPill,
  StatusSectionsProvider,
} from "@/system-status/status-detail-primitives";
import { ConnectionList } from "@/system-status/status-connection-list";
import {
  resolveDomainMetric,
  metricThresholdState,
  resolveResourceMetric,
  useMetricCatalog,
  type MetricCatalog,
} from "@/system-status/use-metric-catalog";
import { StatusConnectionSections } from "@/entity-details/status-connection-sections";
import {
  hostsComponentLevelRoster,
  hostsInterfaceLayer,
  isUserInterfaceManagerNode,
} from "@/entity-details/status-detail-scoping";
import { StatusStreamSections } from "@/entity-details/status-stream-sections";
import { SectionShell } from "@/entity-details/section-shell";
import { StatusQuarantineSection } from "@/entity-details/status-quarantine-section";
import { StatusTrendSection } from "@/entity-details/status-trend-section";
import { PeerManagementDialog } from "@/peers/peer-management-dialog";
import { usePeers } from "@/peers/use-peers";
import { ProviderManagementDialog } from "@/providers/provider-management-dialog";
import { useProviders } from "@/providers/use-providers";

/**
 * Description text for the three health-rollup rows, disclosed through the
 * shared tooltip primitive like every other row description (REQ-UI-133), so
 * these and the metric catalog share one accessibility story instead of
 * inventing two.
 *
 * # Register: operator, not engineer (architect, 2026-08-01)
 *
 * ⚠ These sentences were previously **lifted verbatim from REQ-STATUS-027**,
 * and that instruction is now **superseded**. The architect supplied the
 * "Serving" and "Overall" wording below directly, on the review finding that
 * the requirement's text is written for an engineer: it named the collector's
 * internal inputs, named the backing products, and explained a rendering rule
 * ("the text below names the input") that an operator has no use for. One plain
 * sentence per row, no implementation vocabulary and no specification
 * identifiers — an ADR or REQ id belongs in a code comment, never in front of
 * an operator. REQ-STATUS-027's fixed-text clause needs re-wording to match;
 * until it does, **this table is the truth and the requirement is stale**.
 *
 * "Process" carries a description for the first time: it previously rendered
 * the only tooltip-less row in a pane where every sibling had one, which reads
 * as a missing affordance rather than a deliberate silence.
 */
// ⚠ RESTORED 2026-08-18, having been removed the day before on a false reading.
// A stale local `node_modules` (eslint 9.39.4 / react-refresh 0.4.26 against a
// lockfile pinning 10.8.1 / 0.5.4) reported this directive as UNUSED, so it was
// deleted as dead. Under the pinned toolchain the rule genuinely fires here —
// this module exports a constant table alongside its components — and CI failed
// on exactly that. The directive is load-bearing; keep it.
//
// The lesson is about the TOOL, not this line: a lint or format verdict from an
// out-of-sync `node_modules` is not evidence about CI. Run `npm ci` first.
// eslint-disable-next-line react-refresh/only-export-components -- constant table co-located with its component; see eslint.config.js
export const HEALTH_ROW_DESCRIPTIONS = {
  process: "Indicates whether or not the component is running.",
  serving: "Indicates whether or not the component is functional",
  // NOTE: the `overall` entry is GONE with the row it described (architect,
  // 2026-08-01 — the row duplicated the pane header's badge). Deliberately not
  // kept "in case it comes back": operator-facing text with nothing rendering it
  // is exactly the dead-string class the metric-catalog coverage guard exists to
  // catch, and a future reader would reasonably assume a row is missing rather
  // than that the text is.
} as const;

/** Format a domain-metric number compactly (integers as-is, else 2 dp). */
function formatMetric(value: number): string {
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
}

/** Props for :func:`StatusComponentSections`. */
export interface StatusComponentSectionsProps {
  /** The selected topology node (resolved by the panel wrapper). */
  node: TopologyNode;
  /** The node's resolved stoplight state (shared with the topology). */
  state: RollupState;
  /** Every component instance behind the node, across all of its kinds. */
  components: readonly ResolvedComponent[];
  /**
   * The instance the sections describe — the one whose rollup won the node's
   * stoplight (:func:`primaryComponent`), or undefined when nothing reports.
   */
  primary: ResolvedComponent | undefined;
  /**
   * The ``component_id`` a **component-level** connection roster is fetched
   * under, or null when this node's roster lives on its interfaces / it has
   * none (:func:`componentRosterId` — the first-of-kind scoping fix).
   */
  rosterComponentId: string | null;
  /** Select one of the node's peers → a ``status-peer`` focus (REQ-STATUS-081). */
  onSelectPeer: (peerName: string) => void;
}

/**
 * Route the selected component node to its sectioned detail — a planned
 * placeholder, an "unknown" note, or the full section set. Hosts the Peer Manager
 * management dialog for egress nodes.
 */
export function StatusComponentSections({
  node,
  state,
  components,
  primary,
  rosterComponentId,
  onSelectPeer,
}: StatusComponentSectionsProps): React.ReactElement {
  // The Peer Manager management surface (ADR-036 §D-5), hosted here now that the
  // detail lives in Entity Details. ``null`` = closed; ``{ name }`` = open,
  // optionally pre-selected by interface name.
  const [peerSelection, setPeerSelection] = useState<{ name: string | null } | null>(null);
  const managePeer =
    !node.planned && isPeerNode(node)
      ? (name: string | null): void => setPeerSelection({ name })
      : undefined;

  // The Provider configuration surface (ADR-040 §D-1; Flow V §3), hosted here
  // for the reason its egress twin is: this pane is the node's Selection Detail,
  // and configuration is one thing an operator manages while health is many
  // things they watch. Same shape as `peerSelection` above — `null` = closed,
  // `{ name }` = open, optionally pre-selected by provider or interface name.
  const [providerSelection, setProviderSelection] = useState<{ name: string | null } | null>(null);
  const manageProvider =
    !node.planned && isProviderNode(node)
      ? (name: string | null): void => setProviderSelection({ name })
      : undefined;

  return (
    // Session-local collapse for every section below (REQ-UI-131). The provider
    // wraps the whole pane rather than each section so one store spans them —
    // including the connection drawers, which inherit it (provide-if-absent).
    <StatusSectionsProvider>
      {managePeer && (
        <div className="border-b border-border px-4 py-2">
          <button
            type="button"
            // Unscoped from the node: a node is a *kind*, and the surface is
            // unified across every interface (feature-spec §2.9).
            onClick={() => managePeer(null)}
            data-testid="manage-peers"
            className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
          >
            Peers…
          </button>
        </div>
      )}

      {/* REQ-UI-156's header placement. Always present on an ingress node, every
          role — a deployment that has configured no providers composes no
          provider rows at all, and that is exactly the deployment where an
          operator needs to add one (and the one collecting nothing). */}
      {manageProvider && (
        <div className="border-b border-border px-4 py-2">
          <button
            type="button"
            // Unscoped from the node, like its egress twin: a node is a *kind*,
            // and the surface is unified across every provider.
            onClick={() => manageProvider(null)}
            data-testid="manage-providers"
            className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
          >
            Providers…
          </button>
        </div>
      )}

      {node.planned ? (
        <PlannedPane node={node} />
      ) : (
        <>
          {/* One roster, never two (architect, 2026-08-01). On a node with an
              interface layer the reporting components ARE its interfaces, so
              rendering both sections listed the same flows twice, each with its
              own rollup badge. The interface roster is the one that survives:
              it is authoritative for existence and it navigates. The
              config-roster fetch lives inside that section, so it is still
              issued only for a node that has an interface layer. */}
          {hostsInterfaceLayer(node) ? (
            <PeersSection onSelectPeer={onSelectPeer} onManagePeer={managePeer} />
          ) : (
            <ReportingComponents components={components} />
          )}
          {/* The ingress roster, alongside the reporting components rather than
              instead of them: an ingress node's reporting components are its
              *services*, not its providers, so the two lists describe different
              things and neither duplicates the other. (Contrast the egress node,
              where the reporting components ARE the interfaces.) */}
          {manageProvider && <ProvidersSection onManageProvider={manageProvider} />}
          {primary === undefined ? (
            <p className="px-4 py-3 text-body-sm text-text-muted" data-testid="component-unknown">
              Not collected — the snapshot reports no {node.name} component. Shown as unknown (never
              a false green).
            </p>
          ) : (
            <>
              <ComponentVitalsSections component={primary} nodeState={state} />
              {/* Directly after Domain metrics, which is where `quarantined_total`
                  is rendered: this section is that metric's breakdown, and its
                  reconciliation note ("the Domain metrics quarantined_total counts
                  every quarantine since each process started") only reads as an
                  explanation while the two are adjacent.

                  Gated on PROVIDER_KINDS — the ingest kinds — because quarantine
                  by reason is an ingest-side question (REQ-STATUS-097) and the
                  table's `interface_id` column means something different on the
                  egress side. Mounting it everywhere would put a section on the
                  BFF and Postgres panes whose only content is "not applicable". */}
              {PROVIDER_KINDS.has(primary.component_kind as ComponentKind) && (
                <StatusQuarantineSection
                  componentId={primary.component_id}
                  entityLabel={node.name}
                />
              )}
              {hostsComponentLevelRoster(node) && (
                <ConnectionRosterSection componentId={rosterComponentId} />
              )}
              <StatusTrendSection
                componentId={primary.component_id}
                componentKind={primary.component_kind}
                entityLabel={node.name}
                // The SAME key order Domain metrics renders, so the two sections
                // list one component's metrics once, in one order.
                domainOrder={Object.keys(primary.domain ?? {})}
              />
              {/* The client-observed live-stream states belong to the User Interface
                  Manager because the BFF *is* that component and it serves both
                  EventSources (REQ-STATUS-050). Deliberately last: every section above
                  is collector-gathered server telemetry, and this one is what THIS
                  browser observes — the section labels that distinction, and placing it
                  after the server-reported sections keeps the two from reading as one
                  continuous set of facts about the deployment. */}
              {isUserInterfaceManagerNode(node) && <StatusStreamSections />}
            </>
          )}
        </>
      )}

      <PeerManagementDialog
        open={peerSelection !== null}
        initialSelectionName={peerSelection?.name ?? null}
        onOpenChange={(open) => {
          if (!open) setPeerSelection(null);
        }}
      />

      <ProviderManagementDialog
        open={providerSelection !== null}
        initialSelectionName={providerSelection?.name ?? null}
        onOpenChange={(open) => {
          if (!open) setProviderSelection(null);
        }}
      />
    </StatusSectionsProvider>
  );
}

/**
 * The configured providers behind an ingress node, each handing over to the
 * management surface (REQ-UI-156; Flow V §3).
 *
 * Rendered for **every** authenticated role, field-limited by the BFF: an
 * operator who cannot see the flows entering the system cannot notice one that
 * has stopped, and on ingress a stopped flow is simply quiet. The per-provider
 * `Manage` control is the handover the storyboard's entry point 3 describes —
 * it resolves a provider name directly against the dialog's own roster, so it
 * costs no privileged field and no extra request.
 */
function ProvidersSection({
  onManageProvider,
}: {
  /** Open the surface pre-selected on one provider, or unscoped with `null`. */
  onManageProvider: (providerName: string | null) => void;
}): React.ReactElement {
  const roster = useProviders();
  const providers = roster.data ?? [];
  if (roster.isLoading) {
    return (
      <SectionShell id="status-providers" title="Providers">
        <p role="status" className="text-body-sm text-text-secondary">
          Loading providers…
        </p>
      </SectionShell>
    );
  }
  if (roster.isError) {
    // Honest failure, never an empty list that reads as "none configured"
    // (CLAUDE.md §8.1). On ingress the two have opposite implications: one is a
    // decision, the other is an outage.
    return (
      <SectionShell id="status-providers" title="Providers">
        <p role="alert" className="text-body-sm text-destructive" data-testid="providers-error">
          Could not load the provider configuration: {roster.error.message}
        </p>
      </SectionShell>
    );
  }
  return (
    <SectionShell id="status-providers" title="Providers">
      {providers.length === 0 ? (
        <p className="text-body-sm text-text-muted" data-testid="providers-empty">
          No providers are configured. Nothing is feeding this deployment.
        </p>
      ) : (
        <ul data-testid="detail-providers" className="flex flex-col gap-1">
          {providers.map((provider) => (
            <li
              key={provider.provider_id}
              className="flex items-center gap-2 text-body-sm"
              data-testid={`provider-row-${provider.name}`}
            >
              <span className="min-w-0 flex-1 truncate px-1 font-mono text-text-primary">
                {provider.name}
              </span>
              {/* The roster's own counts (REQ-UI-156), served rather than
                  derived — a client deriving them would need one request per
                  provider, and a non-privileged one could not derive them at
                  all. Absent rather than zeroed when they did not arrive. */}
              {provider.interface_count !== undefined && (
                <span className="flex-none text-meta text-text-muted">
                  {provider.started_interface_count ?? 0}/{provider.interface_count} started
                </span>
              )}
              <button
                type="button"
                onClick={() => onManageProvider(provider.name)}
                aria-label={`Manage ${provider.name}`}
                data-testid={`manage-provider-${provider.name}`}
                className="flex-none rounded border border-border px-1.5 py-0.5 text-meta text-text-secondary hover:bg-bg-elevated"
              >
                Manage
              </button>
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}

/**
 * The components reporting behind one node, each with its own stoplight — so a
 * deliberately-silenced flow among several is never hidden by the node's roll-up
 * (Flow L §11; ``security.md``).
 *
 * **Not rendered on a node with an interface layer.** There the reporting
 * components are the node's interfaces, listed once by
 * :func:`InterfacesSection` (architect, 2026-08-01).
 */
function ReportingComponents({
  components,
}: {
  components: readonly ResolvedComponent[];
}): React.ReactElement | null {
  // Only when there is genuinely MORE THAN ONE instance to compare (architect,
  // 2026-08-01).
  //
  // At one instance this section was a single row whose badge duplicated the pane
  // header's — the header is scoped to that same component — and whose only
  // unique content was the process name. That name now sits under the pane title
  // where it is read once, so the one-row form has nothing left to say.
  //
  // ⚠ The multi-instance form is NOT redundant and is deliberately kept. Under
  // ADR-020 a request-serving service runs 3/3 LB-fronted, and the folded v2
  // Peer Manager node covers three kinds; there the header shows the WORST rollup while
  // these rows show each instance, so "2 of 3 healthy" is visible only here.
  // Removing the section outright would have been invisible on the single-instance
  // testbed and a real loss of operator visibility on a clustered deployment
  // (CLAUDE.md §8.1).
  if (components.length <= 1) return null;
  return (
    <SectionShell id="instances" title="Reporting components">
      <ul data-testid="detail-instances" className="flex flex-col gap-1">
        {components.map((component) => (
          <li
            key={`${component.component_id}::${component.instance_id}`}
            className="flex items-center gap-2 text-body-sm"
          >
            <span className="min-w-0 flex-1 truncate font-mono text-text-primary">
              {component.component_id}
            </span>
            <StatusPill state={parseRollupState(component.rollup_state)} />
          </li>
        ))}
      </ul>
    </SectionShell>
  );
}

/**
 * The Peer Manager node's configured **peer** roster (REQ-STATUS-081 / -042).
 *
 * It listed *interfaces* until ADR-039 Phase I. The stack beside this node now
 * enumerates peers, and a pane that answered a different question from the icons
 * next to it made the operator hold two models of one node at once — so the
 * roster follows the stack. An interface is still reachable, one level in, from
 * the peer that owns it (REQ-STATUS-086).
 *
 * **Existence comes from the config roster** (feature-spec §2.2) — the same rule
 * the icon stack composes by, so the roster and the icons never disagree about
 * what exists. A peer the operator just created is listed at once rather than
 * being invisible until its first sample.
 *
 * # A navigation affordance, not a status display (architect, 2026-08-01)
 *
 * The rows carry **no rollup badge**. A peer's health is what the operator sees
 * when they select that peer — on the diagram or from here — and repeating it on
 * the hosting component's pane put the same set of rollups on screen twice. A
 * row's job on this pane is to say the peer exists and to open it.
 *
 * Owns its own roster fetch (all-roles field-limited read, REQ-PEER-042) rather
 * than taking it as a prop. The query key is shared with the System display's
 * icon stack and the management dialog, so TanStack Query dedups all three to
 * one request (REQ-UI-026 / REQ-UI-035 — BFF-only, and no duplicate of it).
 */
function PeersSection({
  onSelectPeer,
  onManagePeer,
}: {
  onSelectPeer: (peerName: string) => void;
  /**
   * Open the management surface pre-selected on one peer. The dialog resolves a
   * peer name directly against its own roster, so this handover costs no
   * privileged field and no extra request.
   */
  onManagePeer?: (peerName: string | null) => void;
}): React.ReactElement {
  const roster = usePeers();
  const peers = roster.data ?? [];
  if (roster.isLoading) {
    return (
      <SectionShell id="status-peers" title="Peers">
        <p role="status" className="text-body-sm text-text-secondary">
          Loading peers…
        </p>
      </SectionShell>
    );
  }
  if (roster.isError) {
    // Honest failure, never an empty list that reads as "none configured"
    // (CLAUDE.md §8.1) — an operator must not conclude a peer was deleted
    // because its roster failed to load.
    return (
      <SectionShell id="status-peers" title="Peers">
        <p role="alert" className="text-body-sm text-destructive" data-testid="peers-error">
          Could not load the peer configuration: {roster.error.message}
        </p>
      </SectionShell>
    );
  }
  return (
    <SectionShell id="status-peers" title="Peers">
      {peers.length === 0 ? (
        <p className="text-body-sm text-text-muted" data-testid="peers-empty">
          No peers are configured. Nothing is leaving this deployment.
        </p>
      ) : (
        <ul data-testid="detail-peers" className="flex flex-col gap-1">
          {peers.map((peer) => (
            <li key={peer.peer_id} className="flex items-center gap-2 text-body-sm">
              <button
                type="button"
                onClick={() => onSelectPeer(peer.name)}
                data-testid={`peer-row-${peer.name}`}
                className="min-w-0 flex-1 truncate rounded px-1 text-left font-mono text-text-primary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
              >
                {peer.name}
              </button>
              {/* The counts are the roster's own (REQ-UI-140), served rather than
                  derived — a client deriving them would need one request per peer,
                  and a non-privileged one could not derive them at all. */}
              {peer.interface_count !== undefined && (
                <span className="flex-none text-meta text-text-muted">
                  {peer.started_interface_count ?? 0}/{peer.interface_count} started
                </span>
              )}
              {onManagePeer && (
                <button
                  type="button"
                  onClick={() => onManagePeer(peer.name)}
                  aria-label={`Manage ${peer.name}`}
                  data-testid={`manage-peer-${peer.name}`}
                  className="flex-none rounded border border-border px-1.5 py-0.5 text-meta text-text-secondary hover:bg-bg-elevated"
                >
                  Manage
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}

/**
 * The health / resource / domain block — shared by the **component** pane and
 * the **interface** pane, because an interface's vitals are just its own
 * reporting component's vitals (feature-spec §2.2) and duplicating the block
 * would let the two drift (CLAUDE.md §4.4).
 *
 * ``nodeState`` is passed **only** so a standby node can suppress the amber
 * "not serving" reading (ADR-036 §D-7); every chip in here is otherwise derived
 * from ``component`` so that a chip and the text beside it always describe the
 * same instance (REQ-STATUS-027 defect (a)).
 *
 * # Where the metric labels come from (Phase E)
 *
 * The resource and domain rows below are labelled from the deployment's
 * metric-presentation catalog, resolved by the pair
 * ``(component_kind, metric_key)`` (REQ-STATUS-050 / -053). This is the single
 * fetch site for both panes: the interface pane renders this same block, so one
 * cached query serves every Selection Detail.
 *
 * ⚠ A key the catalog does not resolve renders its **raw wire key** and no
 * tooltip trigger (REQ-STATUS-054) — including while the catalog is loading,
 * has failed, or is gated off. The domain map is open-ended by ADR-035 D6
 * design, so a metric a service starts emitting appears immediately, unlabelled
 * but visible; hiding it would suppress operator-visible data on the strength of
 * a display file being out of date.
 */
export function ComponentVitalsSections({
  component,
  nodeState,
  hiddenDomainKeys = [],
  hiddenDomainNote,
}: {
  component: ResolvedComponent;
  nodeState: RollupState;
  /**
   * Domain keys this entity reports but which do not APPLY to it (REQ-UI-149).
   *
   * ⚠ Not a display preference, and not a filter for noise. The one caller is
   * the interface pane, hiding the spool metrics on an interface that has no
   * spool: the emitter reports them as an honest `0.0` because its rule is
   * "reported when known", and a zero here would read as a drained spool on a
   * path where nothing is ever spooled. Anything hidden this way MUST come with
   * {@link hiddenDomainNote} — an unexplained absence is the failure mode this
   * pane spends most of its copy avoiding.
   */
  hiddenDomainKeys?: readonly string[];
  /** Why the hidden keys are absent. Required in practice whenever any are. */
  hiddenDomainNote?: string;
}): React.ReactElement {
  const catalog = useMetricCatalog().data;
  const hidden = new Set(hiddenDomainKeys);
  const allEntries = Object.entries(component.domain ?? {});
  const domainEntries = allEntries.filter(([key]) => !hidden.has(key));
  // Only worth saying when something was actually withheld: a direct interface
  // that never reported the keys at all has nothing to explain.
  const withheldSomething = domainEntries.length < allEntries.length;
  // Only on a COTS component: a Meridian service that reports no vitals IS a
  // gap, and must keep reading as one.
  const cotsNote = isCotsKind(component.component_kind) ? COTS_RESOURCE_NOTE : undefined;
  // ⚠ REQ-STATUS-027 defect (a): the Overall row used to pair the *node's*
  // worst-of state with *this instance's* reason, so on a node backed by several
  // instances the chip and its explanation could describe different components —
  // a false explanation, which CLAUDE.md §8.1 treats as an operator-visibility
  // defect rather than a cosmetic one. Both now come from this instance.
  const componentState = parseRollupState(component.rollup_state);
  // The "Overall" row is GONE (architect, 2026-08-01). It restated the pane
  // header's badge by construction, not by coincidence: `primaryComponent` scopes
  // this pane to the component whose rollup WON the node's stoplight, so the row's
  // state and the header badge were one value rendered twice.
  //
  // What the row uniquely carried — `rollup_reason`, the answer to "why is this
  // not green?" — moved to that header badge's tooltip rather than being deleted
  // with it (`headerRollupTitle` in `entity-details-panel.tsx`), along with the
  // two guards recorded as REQ-STATUS-027 defects (a) and (b). The remaining two
  // rows are the rollup's *inputs*, which the header does not show.
  return (
    <>
      <SectionShell id="status-health" title="Health rollup">
        <BoolStateRow
          testId="rollup-input-process"
          label="Process"
          value={component.up}
          badLabel="down"
          bad="unhealthy"
          description={HEALTH_ROW_DESCRIPTIONS.process}
        />
        <BoolStateRow
          testId="rollup-input-serving"
          label="Serving"
          value={component.ready}
          badLabel="not serving"
          description={HEALTH_ROW_DESCRIPTIONS.serving}
          // A standby component is not-ready BY DESIGN (a stopped Egress
          // Interface reports up=true, ready=false); the amber row beside a grey
          // "Standby" pill would tell two different stories (ADR-036 §D-7).
          bad={componentState === "standby" || nodeState === "standby" ? "standby" : "degraded"}
        />
      </SectionShell>

      <SectionShell id="status-resource" title="Resource utilization">
        {/* `1fr max-content` — the label column takes the slack so the VALUES
            land on the right edge, matching Domain metrics below and the badge
            column in Health rollup above. The columns were the other way round,
            which put resource values immediately after their labels while every
            other reading in the pane was right-aligned (architect, 2026-08-01). */}
        <dl className="grid grid-cols-[1fr_max-content] gap-x-4 gap-y-1 text-body-sm">
          {/* "of host" marks a percentage taken against the host basis rather
              than a cgroup limit — the container sets no limit, so the reading
              is host headroom, not per-service saturation. Disclosure is
              required, not cosmetic (REQ-STATUS-006 / -008 amended 2026-08-01). */}
          <CatalogResourceRow
            catalog={catalog}
            uncollectedNote={cotsNote}
            field="cpu_pct"
            value={component.resource.cpu_pct}
            suffix="%"
            qualifier={
              resourcePercentBasis(
                component.resource.cpu_pct,
                component.resource.cpu_limit_cores,
              ) === "host"
                ? "of host"
                : undefined
            }
          />
          <CatalogResourceRow
            catalog={catalog}
            uncollectedNote={cotsNote}
            field="mem_pct"
            value={component.resource.mem_pct}
            suffix="%"
            qualifier={
              resourcePercentBasis(
                component.resource.mem_pct,
                component.resource.mem_limit_bytes,
              ) === "host"
                ? "of host"
                : undefined
            }
          />
          <CatalogResourceRow
            catalog={catalog}
            uncollectedNote={cotsNote}
            field="fd_count"
            value={component.resource.fd_count}
          />
          <CatalogResourceRow
            catalog={catalog}
            uncollectedNote={cotsNote}
            field="event_loop_lag_ms"
            value={component.resource.event_loop_lag_ms}
            suffix=" ms"
          />
        </dl>
      </SectionShell>

      <SectionShell id="status-domain" title="Domain metrics">
        {domainEntries.length === 0 ? (
          <p className="text-body-sm text-text-muted">No domain metrics reported.</p>
        ) : (
          <dl className="grid grid-cols-[1fr_max-content] gap-x-4 gap-y-1 text-body-sm">
            {domainEntries.map(([key, value]) => {
              // Resolved by the PAIR: `connections_active` and friends mean
              // different things on different kinds, and a flat metric→label map
              // would put a confidently wrong sentence in front of an operator
              // (REQ-STATUS-053).
              const resolved = resolveDomainMetric(catalog, component.component_kind, key);
              return (
                // The test id keys off the raw metric KEY, never the label: the
                // catalog turns the label into deployment configuration
                // (REQ-STATUS-057). Every reported key renders a row whether or
                // not the catalog resolves it (REQ-STATUS-054).
                <MetricRow
                  key={key}
                  testId={`metric-${key}`}
                  label={resolved?.label ?? key}
                  value={formatMetric(value)}
                  description={resolved?.tooltip}
                  // The metric pushing the component off green is marked HERE,
                  // where an operator reads its value — not only in the header
                  // badge's tooltip, which they have to know to hover.
                  breach={metricThresholdState(value, resolved)}
                  // WHICH source produced a worst-of value (REQ-STATUS-103):
                  // `consumer_lag` is the max across every JetStream consumer,
                  // and the response differs by consumer (render / spool /
                  // writer). Keyed by the same wire key as the metric.
                  detail={component.details?.[key]}
                />
              );
            })}
          </dl>
        )}
        {withheldSomething && hiddenDomainNote !== undefined && (
          <p className="pt-1 text-meta text-text-muted" data-testid="domain-metrics-withheld">
            {hiddenDomainNote}
          </p>
        )}
      </SectionShell>
    </>
  );
}

/**
 * One ``ResourceMetrics`` field, labelled from the catalog's own ``resource``
 * namespace (REQ-STATUS-053 — typed struct members, not domain-map keys, so
 * they never fall through to ``global``).
 *
 * ``testId`` is derived from the **wire field name**, which is what closes the
 * REQ-STATUS-027(c) trap :func:`ResourceRow`'s default left open: a label-derived
 * id would differ between two deployments that relabel the same field, and a
 * suite whose selectors match nothing goes green (REQ-STATUS-057).
 */
/**
 * What a COTS component's absent process vitals mean — the row reads
 * *"not collected — COTS"*.
 *
 * Four consecutive "not collected" rows read as broken telemetry. They are not:
 * NATS, PostgreSQL and MinIO do not run ``meridian_status``, so the collector
 * observes them from outside via each product's admin API — which yields their
 * domain metrics and can never yield another process's cgroup. Saying so turns
 * a suspected gap into a category difference (architect, 2026-08-03).
 *
 * Deliberately **one word** (architect, 2026-08-03). The full explanation is
 * this docstring's job, not four repetitions of a sentence down the side of a
 * cramped panel; "COTS" is the term the rest of the pane already uses, and the
 * Identity group names the product itself a few rows above.
 */
export const COTS_RESOURCE_NOTE = "— COTS";

function CatalogResourceRow({
  catalog,
  field,
  value,
  suffix,
  qualifier,
  uncollectedNote,
}: {
  /** The served catalog, or undefined while loading / failed / gated off. */
  catalog: MetricCatalog | undefined;
  /** The ``ResourceMetrics`` field name, exactly as it arrives on the wire. */
  field: string;
  value: number | null | undefined;
  /** Unit suffix appended to the value, unchanged by the catalog. */
  suffix?: string;
  /** Denominator disclosure for percentage rows (REQ-STATUS-006). */
  qualifier?: string;
  /** Structural-absence note, for components that report no process vitals. */
  uncollectedNote?: string;
}): React.ReactElement {
  const resolved = resolveResourceMetric(catalog, field);
  return (
    <ResourceRow
      testId={`resource-${field}`}
      label={resolved?.label ?? field}
      value={value}
      suffix={suffix}
      description={resolved?.tooltip}
      qualifier={qualifier}
      uncollectedNote={uncollectedNote}
      // Re-evaluated from the collector's OWN threshold, served on the catalog
      // entry — not parsed out of `rollup_reason`'s sentence.
      breach={metricThresholdState(value, resolved)}
    />
  );
}

/**
 * The connection roster section — the shared ``status-connections`` shell used
 * at **both** homes (component-level here, interface-level in
 * :func:`StatusInterfaceSections`), so the two can never drift in markup,
 * spacing, collapse behaviour or keyboard handling (REQ-STATUS-044).
 *
 * Rows expand **in place** to a drawer rather than promoting the connection to
 * its own bus selection: the expansion is a disclosure within the host's
 * Selection Detail, so the host stays the single inspected entity and ADR-024
 * §5's one-focus contract is unaffected (REQ-STATUS-039 / -044 amended
 * 2026-07-31).
 */
export function ConnectionRosterSection({
  componentId,
  height = 320,
}: {
  componentId: string | null;
  height?: number;
}): React.ReactElement {
  return (
    <SectionShell id="status-connections" title="Connections">
      {componentId === null ? (
        <p className="text-body-sm text-text-muted" data-testid="connections-unknown">
          Connection detail is not collected for this component.
        </p>
      ) : (
        <ConnectionList
          componentId={componentId}
          height={height}
          renderDrawer={(connection: StatusConnection) => (
            <StatusConnectionSections connection={connection} componentId={componentId} />
          )}
        />
      )}
    </SectionShell>
  );
}

/**
 * A boolean liveness/readiness input as a mini-state row.
 *
 * A satisfied row carries **no detail text**: the row used to append "running"
 * / "serving" beside a "Healthy" badge, which is the badge said twice
 * (architect, 2026-08-01). ``badLabel`` survives because the unsatisfied case is
 * where the badge stops being sufficient — "not serving" beside a *Standby*
 * badge is the sentence that keeps an operator from reading a deliberately
 * stopped peer interface as a fault (REQ-STATUS-014 amended 2026-07-25).
 */
function BoolStateRow({
  testId,
  label,
  value,
  badLabel,
  bad,
  description,
}: {
  testId: string;
  label: string;
  value: boolean | null | undefined;
  /** Detail shown only when ``value`` is false — never on the satisfied row. */
  badLabel: string;
  bad: RollupState;
  /** Row description, disclosed through the shared tooltip (REQ-STATUS-056). */
  description?: string;
}): React.ReactElement {
  if (value === null || value === undefined) {
    // REQ-STATUS-027: `up` / `ready` are nullable and a null is a THIRD state —
    // it must not read as `false`, which would report a healthy component as
    // broken.
    return (
      <MiniStateRow
        testId={testId}
        label={label}
        state="unknown"
        detail="Not collected"
        description={description}
      />
    );
  }
  return value ? (
    <MiniStateRow testId={testId} label={label} state="healthy" description={description} />
  ) : (
    <MiniStateRow
      testId={testId}
      label={label}
      state={bad}
      detail={badLabel}
      description={description}
    />
  );
}

/**
 * Optional extra copy for a grayed placeholder pane, keyed by node id.
 *
 * **Empty by design.** It held a paragraph for `tasking-manager` describing the
 * tasking route through the pipeline; the architect removed it on 2026-08-01.
 * Two things were wrong with it. It named internal components an operator has no
 * vocabulary for (`BFF → Tasking → Message Bus → ingress`), which the canvas
 * copy rules forbid everywhere else in this surface; and it explained a
 * capability that **does not exist yet**, so the one pane in the product with
 * nothing to report was also the wordiest.
 *
 * The mechanism is kept rather than deleted because a future placeholder may
 * genuinely warrant a line. The rule it now follows: a placeholder says *what it
 * is* — the badge — and stays silent unless there is something an operator can
 * act on.
 */
const PLANNED_DESCRIPTIONS: Record<string, string> = {};

/**
 * The planned-capability pane for a grayed placeholder node.
 *
 * The badge reads **"Future Capability"** (architect, 2026-08-01, replacing
 * "Planned — separate feature"): "separate feature" describes *our* delivery
 * plan, which is not a fact about the system the operator is looking at.
 *
 * The description paragraph renders **only when there is one**. A pane that
 * always emitted a paragraph forced a fallback sentence to exist, and the
 * fallback said nothing the badge had not already said.
 */
function PlannedPane({ node }: { node: TopologyNode }): React.ReactElement {
  const description = PLANNED_DESCRIPTIONS[node.id];
  return (
    <div className="p-4">
      <p className="inline-block rounded border border-dashed border-decor-muted px-2 py-0.5 text-meta uppercase tracking-wide text-text-muted">
        Future Capability
      </p>
      {description !== undefined && (
        <p className="mt-2 text-body-sm text-text-secondary" data-testid="planned-description">
          {description}
        </p>
      )}
    </div>
  );
}
